"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Search, ChevronRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import ServiceHeroHeader from "@/components/Services/ServiceHeroHeader";
import Reveal from "@/components/motion/Reveal";
import Link from "next/link";

export default function ManageBookingPage() {
  const router = useRouter();
  const [code, setCode] = useState("");

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!code.trim()) {
      return;
    }
    router.push(`/logopedia-foglalas/manage/${encodeURIComponent(code.trim().toUpperCase())}`);
  }

  return (
    <div className="min-h-screen bg-background">
      <ServiceHeroHeader
        imageSrc="/images/booking/booking-hero.webp"
        imageAlt="Logopédia és gyógytorna foglalások kezelése"
        breadcrumbItems={[
          { label: "Főoldal", href: "/" },
          { label: "Foglalás Kezelése" },
        ]}
        badge="Ügyfélkapu"
        title="Keressen rá Foglalására"
        description="A megadott kód segítségével megtekintheti, módosíthatja vagy lemondhatja logopédiai vagy gyógytorna-foglalását."
      />

      <section className="py-20 px-4">
        <div className="max-w-xl mx-auto">
          <Reveal>
            <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                  <Search className="w-6 h-6" />
                </div>
                
                <h2 className="text-2xl font-display font-bold mb-2">Adja meg foglalási kódját</h2>
                <p className="text-muted-foreground text-sm mb-8">
                  A kódot a foglalást követően kapott visszaigazoló e-mailben találja meg.
                </p>

                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                        <label htmlFor="booking-code" className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Foglalási kód</label>
                        <input
                            id="booking-code"
                            maxLength={8}
                            pattern="[A-Z0-9]{8}"
                            title="A visszaigazolásban kapott 8 karakteres foglalási kód"
                            className="w-full rounded-2xl border-2 border-border bg-background px-4 py-5 text-center text-base sm:text-xl font-mono font-bold uppercase text-primary placeholder:text-muted-foreground/40 focus:border-accent focus:ring-4 focus:ring-accent/10 transition-all outline-hidden"
                            placeholder="ABC12345"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            autoCapitalize="characters"
                            spellCheck={false}
                            required
                        />
                    </div>
                    
                    <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full py-8 rounded-2xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-lg shadow-lg hover:shadow-xl transition-all"
                    >
                        FOGLALÁS KERESÉSE
                    </Button>
                </form>
              </div>
              
              <div className="bg-muted/30 p-6 border-t border-border flex items-start gap-4">
                <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                    Problémája van a kóddal? Ha nem találja e-mailjei között, vegye fel velünk a kapcsolatot telefonon, és munkatársaink segítenek azonosítani foglalását.
                </p>
              </div>
            </div>
          </Reveal>
          
          <Reveal delay={0.2}>
            <div className="mt-12 text-center">
                <p className="text-sm text-muted-foreground mb-4">Még nincs foglalása?</p>
                <Button variant="outline" className="rounded-full px-8 border-border hover:bg-secondary transition-colors" asChild>
                    <Link href="/logopedia-foglalas">
                        Új időpontot foglalok <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                </Button>
                <Button variant="outline" className="mt-3 rounded-full px-8 border-border hover:bg-secondary transition-colors" asChild>
                    <Link href="/gyogytorna-foglalas">Gyógytorna időpontot foglalok <ChevronRight className="w-4 h-4 ml-1" /></Link>
                </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
